import React, { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import Select from "react-select";

import OfferAPI from "../../../../api/OfferAPI";
import { OFFER_PLATFORM } from "../../../../api/OfferAPI.types";
import { getSelectClassNames } from "../../../../components/form/Select";
import Spinner from "../../../../components/utils/Spinner";
import { IMAGE_FORMATS } from "../../../../constants/ratio";
import ImageFormatSelector from "../ImageFormatSelector";

import type { JSX } from "react";

import type { OfferPlanListItem } from "../../../../api/OfferAPI.types";
import type {
  HighlightParamsOffer,
  Platform,
} from "../../../../api/ProjectAPI.types";
import type {
  HorizontalImageFormat,
  ImageFormat,
} from "../../../../constants/ratio";

interface OfferOption {
  value: string;
  label: string;
}

interface HighlightOfferConfigProps {
  componentParams: HighlightParamsOffer;
  onChange: (
    params:
      | Pick<HighlightParamsOffer, "offerPlanId">
      | Pick<HighlightParamsOffer, "ratio">,
  ) => void;
}

function HighlightOfferConfig({
  componentParams,
  onChange,
}: HighlightOfferConfigProps): JSX.Element {
  const { t } = useTranslation("editor-highlight");
  const { projectSlug, platform } = useParams<{
    projectSlug: string;
    platform: Platform;
  }>();

  const isHorizontalImageFormat = (
    format: ImageFormat,
  ): format is HorizontalImageFormat =>
    [
      IMAGE_FORMATS.FORMAT_16_6,
      IMAGE_FORMATS.FORMAT_16_9,
      IMAGE_FORMATS.FORMAT_16_3,
    ].includes(format as HorizontalImageFormat);

  const offerPlatform = useMemo(() => {
    switch (platform) {
      case "web":
        return OFFER_PLATFORM.OKAST;
      case "android":
        return OFFER_PLATFORM.GOOGLE;
      case "ios":
        return OFFER_PLATFORM.GOOGLE;
      default:
        return OFFER_PLATFORM.OKAST;
    }
  }, [platform]);

  const getOfferPlansQuery = OfferAPI.generateQuery({
    fn: OfferAPI.getOfferPlans,
    params: [{ projectSlug, offerPlatform }],
  });
  const { isLoading: isOfferPlansListLoading, data: offerPlansList } = useQuery(
    {
      ...getOfferPlansQuery,
      select: (response) => response?.data,
    },
  );

  const getOfferPlanLabel = (offerPlan: OfferPlanListItem): string => {
    const { resolution, currency, offer, price, type } = offerPlan;
    const typePrefix = type === "svod" ? "SVOD" : "TVOD";
    return `${offer.label}: ${typePrefix} - ${resolution}${price && currency ? ` - ${price} ${currency}` : ""}`;
  };

  if (isOfferPlansListLoading) {
    return (
      <div className="m-[1.875rem]">
        <Spinner />
      </div>
    );
  }

  const offerOptionList: OfferOption[] = (offerPlansList ?? []).map(
    (offerPlan: OfferPlanListItem) => ({
      value: offerPlan.uuid,
      label: getOfferPlanLabel(offerPlan),
    }),
  );

  const selectedOffer =
    offerOptionList.find(
      (option) => option.value === componentParams?.offerPlanId,
    ) ?? null;

  const handleChangeRatio = (newRatio: ImageFormat): void => {
    if (isHorizontalImageFormat(newRatio)) {
      onChange({ ratio: newRatio });
    }
  };

  return (
    <div className="my-[0.625rem] flex flex-col">
      <div className="py-[0.625rem] text-[0.9375rem] font-medium">
        {t("offerListAvalaible")}
      </div>
      <div className="w-[350px] max-md:w-full">
        <Select<OfferOption>
          unstyled
          classNames={getSelectClassNames()}
          inputId="select-offer-highlight"
          isLoading={isOfferPlansListLoading}
          options={offerOptionList}
          placeholder={t("selectOffer")}
          styles={{ control: () => ({}) }}
          value={selectedOffer}
          onChange={(selected) => {
            onChange({ offerPlanId: selected?.value ?? null });
          }}
        />
      </div>
      <div className="pt-[0.9375rem] text-[0.9375rem] font-medium">
        {t("aspectRatioImageBackground")}
      </div>
      <ImageFormatSelector
        onChangeRatio={handleChangeRatio}
        selected={componentParams.ratio}
        imageFormatList={[
          IMAGE_FORMATS.FORMAT_16_9,
          IMAGE_FORMATS.FORMAT_16_6,
          IMAGE_FORMATS.FORMAT_16_3,
        ]}
      />
    </div>
  );
}
export default HighlightOfferConfig;
